/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2021 Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { slug } from '../../common';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import type Mithril from 'mithril';
import type Model from 'flarum/common/Model';
import type PaginatedListState from 'flarum/common/states/PaginatedListState';
import type User from 'flarum/common/models/User';
import PostActivityLeaderItem from './PostActivityLeaderItem';
import HomeActivityLeaderboardState from '../states/HomeActivityLeaderboardState';

/**
 * The posts leaderboard, and the base every other leaderboard is built on.
 *
 * `TItem` is what the state hands out and what {@link listItem} renders; `TState` is the state
 * itself. A leaderboard over a different model names both —
 * `extends HomeActivityLeaderboard<Bookshelf, HomeBookshelvesLeaderboardState>` — and then
 * overrides {@link listItem} and {@link stateManager}.
 *
 * Used by:
 * - flarum-ext-orell-rezensionen: HomeReviewsLeaderboard (users), HomeRatingsLeaderboard (literary works)
 * - flarum-ext-orell-bookshelf: HomeBookshelvesLeaderboard (bookshelves)
 */
export default class HomeActivityLeaderboard<
	TItem extends Model = User,
	TState extends PaginatedListState<TItem> = PaginatedListState<TItem>,
> extends Component<Record<string, never>, TState> {
	nDays!: number;

	oninit(vnode: Mithril.Vnode<Record<string, never>, this>) {
		super.oninit(vnode);

		this.state = new this.stateManager();
		this.state.refresh();

		// Setting that gives the time-frame of the leaderboard
		this.nDays = app.forum.attribute(`${slug}.leaderboard_range`);
	}

	view() {
		return (
			<div className="HomeActivityLeaderboard">
				<h2>
					{this.titleText} {this.loading && <LoadingIndicator />}
				</h2>

				{this.state.isEmpty() && <Placeholder text={this.emptyText} />}

				{this.state.hasItems() && [
					<h3>{this.subTitleText}</h3>,
					<ul className="HomeActivityLeaderboard-items">
						{(this.state as unknown as { getAllItems(): TItem[] }).getAllItems().map((item, i) => (
							<li className="HomeActivityLeaderboard-item" key={item.id()} data-id={item.id()}>
								{this.listItem(item, i + 1)}
							</li>
						))}
					</ul>,
				]}
			</div>
		);
	}

	/**
	 * Whether a loading indicator shows beside the title.
	 */
	get loading(): boolean {
		return this.state.isLoading();
	}

	/**
	 * Renders one entry. Subclasses over another model override this.
	 */
	listItem(item: TItem, position: number): Mithril.Children {
		return <PostActivityLeaderItem user={item as unknown as User} position={position} />;
	}

	get titleText(): Mithril.Children {
		return app.translator.trans('glowingblue-orell-thalia-common.forum.home.posts_leaderboard.title');
	}

	get subTitleText(): Mithril.Children {
		return app.translator.trans('glowingblue-orell-thalia-common.forum.home.posts_leaderboard.sub_title', { days: this.nDays });
	}

	get emptyText(): Mithril.Children {
		return app.translator.trans('glowingblue-orell-thalia-common.forum.home.posts_leaderboard.empty_text', { days: this.nDays });
	}

	/**
	 * The state this leaderboard loads its entries from. Subclasses return their own.
	 */
	get stateManager(): new () => TState {
		return HomeActivityLeaderboardState as unknown as new () => TState;
	}
}
